import React, { Component, ErrorInfo } from 'react';
import axios from 'axios';
import { msalInstance } from './auth-config';
import { RawIndexMap, RawSourceMap, SourceMapConsumer } from 'source-map';
import { apiFetch } from './adalConfig';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import BugReportIcon from '@mui/icons-material/BugReport';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import Collapse from '@mui/material/Collapse';

/*
 * Matches both of these bundle files (with or without 'bundle' in the filename)
 * /dist/main.e82625b44fbef4192a15.bundle.js
 * /dist/main.e82625b44fbef4192a15.js
 */
const regexForMatchingBundleFile = /\/dist\/main\.[A-Za-z0-9]+?\.(?:bundle\.)?js/;

type State = {
  hasError: boolean;
  error: Error | null;
  expanded: boolean;
};

type Props = {
  children: React.ReactNode;
};

class ErrorBoundary extends Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      expanded: false,
    };
  }

  getErrorFromSourceMap = async error => {
    const filePaths = Array.from(document.querySelectorAll('script'))
      .filter(x => x.src.match(regexForMatchingBundleFile))
      .map(x => `${x.src.replace(/.*?(\/dist\/.+)/, '$1')}.map`);

    const stackTraceForMainFile = error.stack
      .split('at')
      .filter(x => x.includes('/dist/main'))
      .join();

    const stackTrace = stackTraceForMainFile
      .match(/\d+:\d+/g)
      .map(x => x.split(':').filter(Boolean));

    const { data: rawSourceMap } = await apiFetch<RawSourceMap | RawIndexMap | string>(
      filePaths[0],
    );

    SourceMapConsumer.initialize({
      'lib/mappings.wasm': 'https://unpkg.com/source-map@0.7.3/lib/mappings.wasm',
    });

    return SourceMapConsumer.with(rawSourceMap, null, consumer =>
      stackTrace
        .map(([lineNumber, columnNumber]) => {
          const line = parseInt(lineNumber);
          const column = parseInt(columnNumber);

          return consumer.originalPositionFor({
            line,
            column,
          });
        })
        .filter(d => !!d.source),
    );
  };

  logToSlack = async (error: Error, info: ErrorInfo) => {
    const account = msalInstance.getActiveAccount();
    const stackTraces = await this.getErrorFromSourceMap(error);

    const stackTraceToString = stackTraces
      .map(stackTrace => {
        const sourceFilePathParts = stackTrace.source
          ?.split('/')
          .filter(part => part.length && !part.startsWith('webpack'));

        const fileName = sourceFilePathParts.pop();
        sourceFilePathParts.push(`*${fileName}*`);
        const sourceFilePath = sourceFilePathParts.join('/');

        return `at ${sourceFilePath} line: ${stackTrace.line}, column: ${stackTrace.column}`;
      })
      .join('\n');

    await axios.post(
      'https://hooks.slack.com/services/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',
      JSON.stringify({
        text: `*${location.pathname}${location.search}*
        *User: ${account?.idTokenClaims?.email}*
        ${error.message}
        ${stackTraceToString}
        ${error.stack}`,
      }),
    );
  };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  async componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ hasError: true, error: error });

    if (ENVIRONMENT === 'Production') {
      await this.logToSlack(error, info);
    }
  }

  toggleExpanded = () => {
    this.setState(prevState => ({ expanded: !prevState.expanded }));
  };

  render() {
    const { hasError, error, expanded } = this.state;

    if (!hasError) {
      return this.props.children;
    }

    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert
          severity="error"
          icon={<BugReportIcon sx={{ fontSize: 'large' }} />}
          action={
            <IconButton aria-label="expand" size="small" onClick={this.toggleExpanded}>
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          }
        >
          <AlertTitle>Oops! Something went wrong.</AlertTitle>
          <Typography variant="body2" gutterBottom>
            We've encountered an unexpected issue on this page:&nbsp;
            <Typography component="span" sx={{ fontWeight: 'bold' }}>
              {window.location.href}
            </Typography>
          </Typography>
          <Typography variant="body2" gutterBottom>
            Please try refreshing the page. If the problem persists, please share this error
            information with the support team.
          </Typography>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            {error && (
              <Stack spacing={1} sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Error details:</Typography>
                <Typography component="pre" style={{ whiteSpace: 'pre-wrap' }}>
                  {error && error.message}
                </Typography>
                <Typography variant="subtitle2">Stack trace:</Typography>
                <Typography component="pre" style={{ whiteSpace: 'pre-wrap' }}>
                  {error.stack}
                </Typography>
              </Stack>
            )}
          </Collapse>
        </Alert>
      </Container>
    );
  }
}

export default ErrorBoundary;
